import { useMemo, useEffect } from 'react';
import { useLocationsGroupFilter } from '~/Hooks/useFilter/useLocationsGroupFilter';
import { useActiveEventsFilter } from '~/Hooks/useFilter/useActiveEventsFilter';
import { useGroupFilter, FilterType } from '~/Hooks/useFilter';
import { SegmentName } from '~/Constants/Segment.constants';
import { Segment } from '~/Helpers/Segment.helper';
import { useProvider } from '../../Hooks';
import { useManualBookingTypeFilter } from '~/Hooks/useFilter/useManualBookingTypeFilter';

export interface IUseCheckinFilterProp {
  additionalfilters?: { [filterName: string]: any };
}

type TrackingObject = Record<string, boolean | string | any[]>;

export const useCheckinFilter = ({ additionalfilters }: IUseCheckinFilterProp) => {
  const filterType = FilterType.CHECKIN_FILTER;
  const { currentProvider } = useProvider();
  const providerName = currentProvider?.name ?? '';

  const locationsGroup = useLocationsGroupFilter({ filterType });
  const activeEventGroups = useActiveEventsFilter({ filterType });
  const manualBookingsTypeGroup = useManualBookingTypeFilter({ filterType });

  const groups = useMemo(() => [activeEventGroups, locationsGroup, manualBookingsTypeGroup], [
    locationsGroup,
    activeEventGroups,
    manualBookingsTypeGroup,
  ]);

  const { appliedFilters, ...props } = useGroupFilter({
    groups,
    filterType,
  });

  useEffect(() => {
    if (appliedFilters?.locationIds?.toString()) {
      const trackingObj: TrackingObject = { providerName, timeStamp: new Date().toISOString() };
      Segment.track(SegmentName.CHECKIN_FILTER_LOCATION_APPLY, trackingObj);
    }
  }, [appliedFilters?.locationIds?.toString()]);

  useEffect(() => {
    if (appliedFilters?.eventIds?.toString()) {
      const trackingObj: TrackingObject = { providerName, timeStamp: new Date().toISOString() };
      Segment.track(SegmentName.CHECKIN_FILTER_EVENT_APPLY, trackingObj);
    }
  }, [appliedFilters?.eventIds?.toString()]);

  useEffect(() => {
    if (additionalfilters?.date?.toString()) {
      const trackingObj: TrackingObject = { providerName, timeStamp: new Date().toISOString() };
      Segment.track(SegmentName.CHECKIN_FILTER_DATE_APPLY, trackingObj);
    }
  }, [additionalfilters?.date?.toString()]);

  useEffect(() => {
    if (additionalfilters?.searchText) {
      const trackingObj: TrackingObject = { providerName, timeStamp: new Date().toISOString() };
      Segment.track(SegmentName.CHECKIN_FILTER_SEARCH_APPLY, trackingObj);
    }
  }, [additionalfilters?.searchText]);

  return {
    appliedFilters: { ...appliedFilters, ...additionalfilters },
    ...props,
  };
};
